/**
 * สถานะการกด submit ของหน้าจอหน้างาน — ปลายทางคือ "เข้าคิว" ไม่ใช่ "บันทึกสำเร็จ"
 *
 * แยกออกจาก component เพราะหน้าจอทั้งสามโหมดต้องเล่าเรื่องเดียวกันทุกตัวอักษร:
 * ตรวจที่เครื่องก่อน → เขียนลงคิวในเครื่อง → บอกว่ารอซิงค์อยู่ ไม่มีโหมดไหนที่
 * ได้รับอนุญาตให้พูดว่าเซิร์ฟเวอร์รับแล้ว (D-075, `docs/SECURITY.md`)
 */

import { useCallback, useState } from 'react';

import type { OfflineCommandRecord, OfflineQueueErrorReason } from '../lib/offline/commandEnvelope';
import type { ShopFloorCommand } from '../lib/shopFloor/commands';
import {
  enqueueShopFloorCommand,
  type EnqueueShopFloorCommandResult,
} from '../lib/shopFloor/enqueue';

export type ScanSubmitState =
  | { readonly status: 'idle' }
  /** ตรวจที่หน้าจอไม่ผ่าน — **ยังไม่มีอะไรถูกเขียนลงคิว** */
  | { readonly status: 'invalid'; readonly problems: readonly string[] }
  | { readonly status: 'queuing' }
  /** อยู่ในคิวในเครื่องแล้ว รอ sync engine — ยังไม่ใช่ผลจากเซิร์ฟเวอร์ */
  | { readonly status: 'queued'; readonly record: OfflineCommandRecord }
  | { readonly status: 'failed'; readonly reason: OfflineQueueErrorReason };

/**
 * ผลการประกอบคำสั่งจากค่าที่กรอก
 *
 * หน้าจอเป็นผู้ประกอบ ไม่ใช่ hook เพราะกติกาการตรวจของแต่ละโหมดต่างกัน และ
 * `problems` ต้องเป็นข้อความที่ผู้ปฏิบัติงานอ่านแล้วรู้ว่าจะไปแก้ช่องไหน
 */
export type ScanCommandDraft =
  | { readonly ok: true; readonly command: ShopFloorCommand }
  | { readonly ok: false; readonly problems: readonly string[] };

export interface ScanSubmitController {
  readonly state: ScanSubmitState;
  /** คืนผลของคิวเมื่อมีการเขียนจริง และคืน `null` เมื่อถูกปฏิเสธที่หน้าจอ */
  readonly submit: (draft: ScanCommandDraft) => Promise<EnqueueShopFloorCommandResult | null>;
  readonly reset: () => void;
}

export function useScanSubmit(): ScanSubmitController {
  const [state, setState] = useState<ScanSubmitState>({ status: 'idle' });

  const reset = useCallback(() => {
    setState({ status: 'idle' });
  }, []);

  const submit = useCallback(
    async (draft: ScanCommandDraft): Promise<EnqueueShopFloorCommandResult | null> => {
      if (!draft.ok) {
        setState({ status: 'invalid', problems: draft.problems });
        return null;
      }

      setState({ status: 'queuing' });
      const result = await enqueueShopFloorCommand(draft.command);
      setState(
        result.status === 'queued'
          ? { status: 'queued', record: result.record }
          : { status: 'failed', reason: result.reason },
      );
      return result;
    },
    [],
  );

  return { state, submit, reset };
}
